<template>
	<div class="screen-margin custom-adjustment">
		<p class="step-name">{{ $t("pages.newInviteSupplier.steps.inviteUpdate") }}</p>
		<Timeline :value="stepsWorkflow" class="customized-timeline">
			<template #marker="slotProps">
				<span class="custom-marker shadow-2" :style="{ backgroundColor: slotProps.item.color }">
					<i :class="slotProps.item.icon"></i>
				</span>
			</template>
			<template #content="slotProps">
				<Card class="card" :class="{ 'scroll-y': expandedItems.includes(slotProps.item.id) }">
					<template #title>
						{{ slotProps.item.name }}
						<b-button
							v-if="slotProps.item.details.length"
							variant="outline-secondary"
							size="sm"
							@click="toggleDetails(slotProps.item.id)"
							:aria-expanded="expandedItems.includes(slotProps.item.id)"
							class="ml-2"
						>
							<b-icon
								:icon="slotProps.item.expanded ? 'chevron-up' : 'chevron-down'"
								aria-hidden="true"
								v-b-tooltip.hover="{
									title: slotProps.item.expanded ? $t('commons.hovers.retract') : $t('commons.hovers.expand'),
									customClass: 'custom-tooltip',
									interactive: false,
									placement: 'bottom'
								}"
							></b-icon>
						</b-button>
					</template>
					<template #subtitle>
						{{ slotProps.item.stepMessage }}
					</template>
					<template #content>
						<p class="margin-top: 1rem">
							{{ slotProps.item.message }}
						</p>

						<b-collapse
							:id="'collapse-' + slotProps.item.id"
							:visible="expandedItems.includes(slotProps.item.id)"
							class="mt-1"
						>
							<div v-for="(detail, index) in slotProps.item.details" :key="index" class="mb-2">
								{{ detail }}
							</div>
						</b-collapse>
					</template>
					<template #footer>
						<b-button
							v-if="slotProps.item.details.length && expandedItems.includes(slotProps.item.id)"
							variant="secondary"
							size="md"
							@click="showStepDetails(slotProps.item.idStep, slotProps.item.name, slotProps.item.idHistory)"
						>
							{{ $t("pages.newInviteSupplier.followUp.showDetails") }}
							<b-icon icon="search" aria-hidden="true" class="ml-1"></b-icon>
						</b-button>
					</template>
				</Card>
			</template>
		</Timeline>
	</div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import Timeline from "primevue/timeline";
import Card from "primevue/card";

export default {
	data() {
		return {
			stepsWorkflow: [],
			currentStepColor: {
				[this.$inviteStatus.CANCELED]: "#ED4337",
				[this.$inviteStatus.IN_PROGRESS]: "#00648F",
				[this.$inviteStatus.COMPLETED]: "#5CB85C",
				PREVIOUS: "#D6D6D6"
			},
			currentStepIcon: {
				[this.$inviteStatus.CANCELED]: "pi pi-times-circle",
				[this.$inviteStatus.IN_PROGRESS]: "pi pi-clock",
				[this.$inviteStatus.COMPLETED]: "pi pi-check-circle",
				PREVIOUS: "pi pi-check-circle"
			},
			expandedItems: []
		};
	},
	components: {
		Timeline,
		Card
	},
	props: {
		screenType: {
			type: Number,
			require: true
		},
		inviteId: {
			type: [String, Number]
		}
	},
	computed: {
		...mapGetters(["languages", "protocolDetails"])
	},
	methods: {
		...mapActions(["getWorkflowHistoryDetails"]),
		loadInviteInfos: function (idInvite) {
			this.getWorkflowHistoryDetails(idInvite).then((response) => {
				this.setUpAccompaniment(response);
			});
		},
		setUpAccompaniment({ registers }) {
			let stepList = [];
			let id = 0;

			const firstRegister = registers.find((r) => r.actionStep == this.$ActionStepType.CREATED);
			stepList.push(
				this.getStep({
					id: id,
					name: this.$t("pages.materials.protocolCreated"),
					status: "PREVIOUS",
					message: "",
					register: firstRegister
				})
			);

			registers
				.filter((r) => r.actionStep != this.$ActionStepType.CREATED)
				.forEach((register) => {
					stepList.push(
						this.getStep({
							id: id++,
							idStep: register.idStep,
							name: register.currentStep,
							status: this.mapStatusActionToIcon(register.actionStep),
							message: register.message,
							register: register
						})
					);
				});

			stepList.reverse();
			stepList.forEach((st) => this.stepsWorkflow.push(st));
		},
		getStep({ id, idStep, name, status, message, register }) {
			const { idHistory, userName, impersonatorName, actionStep, when, destinationStep, actions } = register;
			const displayName = this.$functions.formatImpersonatorDisplayName(this, userName, impersonatorName);
			return {
				id,
				idHistory,
				idStep,
				name,
				stepMessage: this.createStepActionMessage(displayName, actionStep, when, destinationStep),
				message,
				icon: this.currentStepIcon[status],
				color: this.currentStepColor[status],
				details: actions?.length ? this.createUserActionsList(actions) : [],
				expanded: false
			};
		},
		createStepActionMessage(userName, actionStep, date, destinationStep) {
			const formatDate = this.$functions.formatDateWorkflowDetails(date);

			switch (actionStep) {
				case this.$ActionStepType.CREATED:
					return `${formatDate} - ${userName} ${this.$t("pages.newInviteSupplier.followUp.userCreateInvite")}`;
				case this.$ActionStepType.PROGRESS:
					return `${formatDate} - ${userName} ${this.$t(
						"pages.newInviteSupplier.followUp.userProgressStep"
					)} ${destinationStep}.`;
				case this.$ActionStepType.CANCELED:
					return `${formatDate} - ${userName} ${this.$t("pages.newInviteSupplier.followUp.userCancelInvite")}`;
				case this.$ActionStepType.CONCLUDED:
					return `${formatDate} - ${userName} ${this.$t("pages.newInviteSupplier.followUp.userConcludeInvite")}`;
			}
		},
		mapStatusActionToIcon(actionStep) {
			switch (actionStep) {
				case this.$ActionStepType.CONCLUDED:
					return this.$inviteStatus.COMPLETED;
				case this.$ActionStepType.CANCELED:
					return this.$inviteStatus.CANCELED;
				default:
					return "PREVIOUS";
			}
		},
		createUserActionsList(userActions) {
			const mapStatusAndActionToText = (actionRequirement, action) => {
				switch (actionRequirement) {
					case this.$ActionRequirementType.RESPONSED:
						return action === "create"
							? this.$t("pages.newInviteSupplier.followUp.userCreateRequirementResponse")
							: this.$t("pages.newInviteSupplier.followUp.userUpdateRequirementResponse");
					case this.$ActionRequirementType.APPROVED:
						return this.$t("pages.newInviteSupplier.followUp.userApproveRequirement");
					case this.$ActionRequirementType.REJECTED:
						return this.$t("pages.newInviteSupplier.followUp.userRejectRequirement");
					case this.$ActionRequirementType.REEVALUATE:
						return this.$t("pages.newInviteSupplier.followUp.userReevalueateRequirement");
					case this.$ActionRequirementType.CHANGED_VALIDITY:
						return this.$t("pages.newInviteSupplier.followUp.userChangeRequirementValidity");
					case this.$ActionRequirementType.RESPONSED_PARTIALLY:
						return this.$t("pages.newInviteSupplier.followUp.userCreatePartialRequirementResponse");
					default:
						return "";
				}
			};

			return userActions.map(({ userName, impersonatorName, propertieName, when, actionRequirement, action, entity }) => {
				if (entity !== "RequirementResponse") return;

				const formatDate = this.$functions.formatDateWorkflowDetails(when);
				const actionText = mapStatusAndActionToText(actionRequirement, action);

				if (actionText === "") return;

				const displayName = this.$functions.formatImpersonatorDisplayName(this, userName, impersonatorName);

				return `${formatDate} - ${displayName} ${actionText} ${propertieName}.`;
			});
		},
		toggleDetails(id) {
			this.stepsWorkflow = this.stepsWorkflow.map((step) =>
				step.id === id ? { ...step, expanded: !step.expanded } : step
			);

			if (this.expandedItems.includes(id)) {
				this.expandedItems = this.expandedItems.filter((itemId) => itemId !== id);
			} else {
				this.expandedItems.push(id);
			}
		},
		showStepDetails(id, name, idHistory) {
			this.$emit("showDetails", { idStep: id, stepName: name, idHistory });
		}
	}
};
</script>

<style scoped>
::v-deep .p-autocomplete-input {
	font-family: Open Sans;
	font-size: 0.75rem !important;
	border-radius: 4px;
}

::v-deep .p-autocomplete-panel {
	font-family: Open Sans;
	font-size: 0.85rem !important;
}

.step-name {
	font-family: "Open Sans";
	font-style: normal;
	font-weight: 600;
	font-size: 16px;
	line-height: 16px;
	color: #1c1637;
}

.screen-margin {
	width: 90%;
	font-size: 12px;
}

.scroll-y {
	overflow-y: auto;
	max-height: 95%;
}

.screen-margin .col {
	padding-left: 9px !important;
}

.wrapper {
	max-height: 162px;
	margin-left: 15px;
}
.wrapper > fieldset {
	margin-bottom: 0.25rem;
}
.wrapper > fieldset > div > div {
	display: flex;
	flex-direction: column;
	overflow-y: scroll;
	max-height: 112px;
	border: 1px solid #666666;
	border-radius: 5px;
	padding: 5px;
}
.wrapper > fieldset > div > div > div {
	display: block;
}

.custom-adjustment {
	padding-left: 9px;
	padding-right: 30px;
}

.custom-marker {
	display: flex;
	width: 2rem;
	height: 2rem;
	align-items: center;
	justify-content: center;
	color: #ffffff;
	border-radius: 50%;
	z-index: 1;
}

:deep(.p-timeline-event-content) {
	line-height: 1;
}

@media screen and (max-width: 960px) {
	.customized-timeline {
		display: flex;
		flex-wrap: wrap;
	}
	.p-timeline-event:nth-child(even) {
		flex-direction: row;
	}
}

:deep(.p-timeline-event-opposite) {
	flex: 0;
}

.card {
	width: 100%;
	height: 95%;
}

.expanded-content {
	padding: 0.5rem;
}
</style>
